import { CompanyAICompetitiveIntelligenceRow } from 'types/recommendations';

const TARGET_ROW_COUNT = 3;

type Comparator = (a: CompanyAICompetitiveIntelligenceRow, b: CompanyAICompetitiveIntelligenceRow) => number;

type Rung = {
  reason: string;
  matches: (row: CompanyAICompetitiveIntelligenceRow) => boolean;
  compare: Comparator;
};

export type NotableCommunity = {
  row: CompanyAICompetitiveIntelligenceRow;
  reason: string;
};

const localMarketSize = (row: CompanyAICompetitiveIntelligenceRow) => (row.competitors_count ?? 0) + 1;

const gapToLeader = (row: CompanyAICompetitiveIntelligenceRow): number | null => {
  if (row.rank == null || row.rank <= 1 || row.score == null || row.top_competitor?.score == null) {
    return null;
  }
  return row.top_competitor.score - row.score;
};

const marginOverSecond = (row: CompanyAICompetitiveIntelligenceRow): number | null => {
  if (row.rank !== 1 || row.score == null || row.top_competitor?.score == null) {
    return null;
  }
  return row.score - row.top_competitor.score;
};

const isInTopFraction = (row: CompanyAICompetitiveIntelligenceRow, fraction: number) =>
  row.rank != null && row.rank <= Math.ceil(localMarketSize(row) * fraction);

const isInBottomFraction = (row: CompanyAICompetitiveIntelligenceRow, fraction: number) => {
  const size = localMarketSize(row);
  return row.rank != null && row.rank > size - Math.ceil(size * fraction);
};

const byAscending =
  (metric: (row: CompanyAICompetitiveIntelligenceRow) => number | null): Comparator =>
  (a, b) => {
    const aValue = metric(a);
    const bValue = metric(b);
    if (aValue == null) return bValue == null ? 0 : 1;
    if (bValue == null) return -1;
    return aValue - bValue;
  };

const byDescending =
  (metric: (row: CompanyAICompetitiveIntelligenceRow) => number | null): Comparator =>
  (a, b) => {
    const aValue = metric(a);
    const bValue = metric(b);
    if (aValue == null) return bValue == null ? 0 : 1;
    if (bValue == null) return -1;
    return bValue - aValue;
  };

const combine =
  (...comparators: Comparator[]): Comparator =>
  (a, b) =>
    comparators.reduce((result, compare) => result || compare(a, b), 0);

const LEADING_LADDER: Rung[] = [
  {
    reason: 'Leads its market',
    matches: (row) => row.rank === 1,
    compare: combine(
      byDescending(marginOverSecond),
      byDescending((row) => row.score)
    ),
  },
  {
    reason: 'Within 2 pts of the lead',
    matches: (row) => (gapToLeader(row) ?? Infinity) <= 2,
    compare: byAscending(gapToLeader),
  },
  { reason: '2nd in its market', matches: (row) => row.rank === 2, compare: byAscending(gapToLeader) },
  {
    reason: 'Top third of its market',
    matches: (row) => localMarketSize(row) >= 6 && isInTopFraction(row, 1 / 3),
    compare: byAscending(gapToLeader),
  },
  {
    reason: 'Top half of its market',
    matches: (row) => localMarketSize(row) >= 6 && isInTopFraction(row, 1 / 2),
    compare: byAscending(gapToLeader),
  },
];

const LAGGING_LADDER: Rung[] = [
  {
    reason: 'Bottom 20% of its market',
    matches: (row) => localMarketSize(row) >= 6 && isInBottomFraction(row, 0.2),
    compare: byDescending(gapToLeader),
  },
  {
    reason: 'Last or next-to-last',
    matches: (row) => localMarketSize(row) >= 5 && row.rank != null && row.rank >= localMarketSize(row) - 1,
    compare: byDescending(gapToLeader),
  },
  {
    reason: 'Bottom third of its market',
    matches: (row) => localMarketSize(row) >= 6 && isInBottomFraction(row, 1 / 3),
    compare: byDescending(gapToLeader),
  },
  {
    reason: "Below its market's median score",
    matches: (row) => row.score != null && row.local_median_score != null && row.score < row.local_median_score,
    compare: byDescending(gapToLeader),
  },
  {
    reason: 'Furthest behind its leader',
    matches: (row) => row.rank != null && row.rank > 1,
    compare: byDescending(gapToLeader),
  },
];

// A community belongs to one table only: the ladder it reaches first wins, ties go to Leading.
const rungIndex = (row: CompanyAICompetitiveIntelligenceRow, ladder: Rung[]) => {
  const index = ladder.findIndex((rung) => rung.matches(row));
  return index === -1 ? Infinity : index;
};

const fillFromLadder = (rows: CompanyAICompetitiveIntelligenceRow[], ladder: Rung[]): NotableCommunity[] => {
  const selected: NotableCommunity[] = [];

  ladder.forEach((rung, index) => {
    if (selected.length >= TARGET_ROW_COUNT) return;

    const candidates = rows.filter((row) => rungIndex(row, ladder) === index);
    candidates.sort(rung.compare);
    candidates
      .slice(0, TARGET_ROW_COUNT - selected.length)
      .forEach((row) => selected.push({ row, reason: rung.reason }));
  });

  return selected;
};

export const selectNotableCommunities = (rows: CompanyAICompetitiveIntelligenceRow[]) => {
  const leads = (row: CompanyAICompetitiveIntelligenceRow) =>
    rungIndex(row, LEADING_LADDER) <= rungIndex(row, LAGGING_LADDER);

  return {
    leading: fillFromLadder(
      rows.filter((row) => leads(row) && rungIndex(row, LEADING_LADDER) !== Infinity),
      LEADING_LADDER
    ),
    lagging: fillFromLadder(
      rows.filter((row) => !leads(row)),
      LAGGING_LADDER
    ),
  };
};
